import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Chip,
  Skeleton,
  Alert,
  Button,
  Stack,
  Divider,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import {
  FactCheck as WorksheetIcon,
  CalendarMonth as WeekIcon,
  Refresh as RefreshIcon,
} from "@mui/icons-material";
import { toast } from "react-toastify";
import api from "../../api";

const subjectColor = (key) =>
  ({
    maths: "#1565c0",
    english: "#6a1b9a",
    "verbal-reasoning": "#2e7d32",
    "non-verbal-reasoning": "#ef6c00",
  }[key] || "#455a64");

/**
 * Worksheets an admin allocated to this student's course-plan weeks, grouped by subject.
 *
 * Distinct from the Work Sheets catalogue tab, which is the open mobile-subscription library.
 * Rendered as the "Assigned to me" tab inside the Work Sheet page; `embedded` drops the page
 * chrome (outer padding and the h5 title) so it sits inside those tabs cleanly.
 */
export default function AssignedWorksheets({ embedded = false }) {
  const navigate = useNavigate();
  const [opening, setOpening] = useState(null);
  const [subjects, setSubjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [filter, setFilter] = useState("all");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await api.get("student/worksheets/assigned");
      setSubjects(res.data?.data?.subjects ?? []);
      setMessage(res.data?.message || "");
    } catch (e) {
      setError(e?.response?.data?.message || "Could not load your worksheets.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  /** Assigned worksheets have no purchase row; the server grants one, then we take it. */
  const openWorksheet = async (paperId) => {
    setOpening(paperId);
    try {
      const res = await api.post(`student/worksheets/assigned/${paperId}/open`);
      const purchaseId = res.data?.data?.purchase_id;
      if (!purchaseId) throw new Error("no purchase id");
      navigate(`/student/past-paper/${purchaseId}/take`);
    } catch (e) {
      toast.error(e?.response?.data?.message || "Could not open this worksheet.");
    } finally {
      setOpening(null);
    }
  };

  const visible = filter === "all" ? subjects : subjects.filter((s) => String(s.subject_id) === filter);
  const total = subjects.reduce((n, s) => n + s.worksheets.length, 0);

  return (
    <Box sx={embedded ? {} : { p: { xs: 2, md: 3 } }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          {!embedded && <WorksheetIcon color="primary" />}
          <Typography variant={embedded ? "subtitle1" : "h5"} fontWeight={700}>
            {embedded ? "Assigned to me" : "My Worksheets"}
          </Typography>
        </Stack>
        <Button startIcon={<RefreshIcon />} onClick={load} disabled={loading}>
          Refresh
        </Button>
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Worksheets assigned to your course, by subject.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Stack spacing={2}>
          {[1, 2].map((n) => (
            <Skeleton key={n} variant="rounded" height={150} />
          ))}
        </Stack>
      ) : total === 0 && !error ? (
        <Alert severity="info">{message || "No worksheets have been assigned to you yet."}</Alert>
      ) : (
        <>
          {subjects.length > 1 && (
            <ToggleButtonGroup
              size="small"
              exclusive
              value={filter}
              onChange={(_, v) => v && setFilter(v)}
              sx={{ mb: 2, flexWrap: "wrap" }}
            >
              <ToggleButton value="all">All ({total})</ToggleButton>
              {subjects.map((s) => (
                <ToggleButton key={s.subject_id ?? "none"} value={String(s.subject_id)}>
                  {s.subject_name} ({s.worksheets.length})
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
          )}

          <Stack spacing={2}>
            {visible.map((s) => (
              <Card key={s.subject_id ?? "none"} variant="outlined" sx={{ borderRadius: 2 }}>
                <CardContent>
                  <Chip
                    label={s.subject_name}
                    sx={{
                      bgcolor: `${subjectColor(s.subject_key)}18`,
                      color: subjectColor(s.subject_key),
                      fontWeight: 700,
                      mb: 1.5,
                    }}
                  />
                  <Divider sx={{ mb: 1.5 }} />
                  <Stack spacing={1}>
                    {s.worksheets.map((w, i) => (
                      <Stack
                        key={`${w.paper_id}-${i}`}
                        direction={{ xs: "column", sm: "row" }}
                        alignItems={{ sm: "center" }}
                        justifyContent="space-between"
                        spacing={1}
                        sx={{ py: 1, borderBottom: "1px solid #f2f2f2" }}
                      >
                        <Box>
                          <Typography fontWeight={600}>{w.name}</Typography>
                          {w.week_number != null && (
                            <Stack direction="row" alignItems="center" spacing={0.5}>
                              <WeekIcon sx={{ fontSize: 15, color: "text.disabled" }} />
                              <Typography variant="caption" color="text.secondary">
                                {w.label || `Week ${w.week_number}`}
                              </Typography>
                            </Stack>
                          )}
                        </Box>
                        <Button
                          size="small"
                          variant="contained"
                          disabled={opening === w.paper_id}
                          onClick={() => openWorksheet(w.paper_id)}
                        >
                          {opening === w.paper_id ? "Opening…" : "Start"}
                        </Button>
                      </Stack>
                    ))}
                  </Stack>
                </CardContent>
              </Card>
            ))}
          </Stack>
        </>
      )}
    </Box>
  );
}
